import { useState } from 'react'
import { Copy, Check, Link2, MessageSquare, Mail } from 'lucide-react'
import { Note } from '@/shared/ui/states'
import type { InvitationIssuedResponse } from '@/shared/api/types'

/**
 * What happened to an invitation, and — in dev — the link itself, ready to copy.
 *
 * ## Why the link is here at all
 *
 * SMS is the primary channel now that the phone number is the identity, and in a development
 * deployment there is usually no SMS integration configured, so nothing is delivered. Without
 * a way to see the link, every invited account is unreachable and the whole invite flow is
 * untestable. The backend returns it only while `security.invitation-return-token` is on.
 *
 * ## It is a credential, and it is labelled as one
 *
 * Anyone holding this link can set that account's password. The panel says so rather than
 * presenting it as a convenience, because an administrator who pastes it into a group chat
 * has handed over the account.
 */
export function InviteLinkPanel({ result, phone, email }: {
  result: InvitationIssuedResponse
  phone: string
  email?: string | null
}) {
  const [copied, setCopied] = useState<'link' | 'token' | null>(null)

  async function copy(text: string, what: 'link' | 'token') {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(what)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      // Clipboard is blocked on insecure origins and in some embedded browsers. The text is
      // already on screen and selectable, so failing silently here loses nothing.
    }
  }

  return (
    <div className="space-y-3">
      {/* Delivery, stated per channel. "Sent" is never claimed for a channel that has none. */}
      <div className="flex flex-wrap gap-2">
        <Channel
          icon={<MessageSquare className="h-3.5 w-3.5" />}
          label={`SMS to ${phone}`}
          ok={result.smsSent}
          okText="accepted by the provider"
          badText="not sent"
        />
        {email
          ? <Channel
              icon={<Mail className="h-3.5 w-3.5" />}
              label={`Email to ${email}`}
              ok={result.emailSent}
              okText="accepted"
              badText="not sent"
            />
          : <span className="inline-flex items-center gap-1.5 text-erp-xs text-muted-foreground border border-border rounded-md px-2 py-1">
              <Mail className="h-3.5 w-3.5" /> No email on file — SMS only
            </span>}
      </div>

      {result.deliveryError && !result.smsSent && (
        <Note tone="bad">{result.deliveryError}</Note>
      )}

      {result.inviteLink ? (
        <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-md p-3 space-y-2">
          <div className="text-erp-sm font-medium text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
            <Link2 className="h-3.5 w-3.5" /> Invitation link
          </div>
          <div className="flex gap-2">
            <code className="flex-1 text-erp-xs font-mono bg-background border border-border rounded-md p-2 break-all">
              {result.inviteLink}
            </code>
            <button
              type="button"
              className="erp-btn shrink-0 self-start"
              onClick={() => copy(result.inviteLink!, 'link')}
            >
              {copied === 'link'
                ? <><Check className="h-3.5 w-3.5" /> Copied</>
                : <><Copy className="h-3.5 w-3.5" /> Copy</>}
            </button>
          </div>
          <p className="text-erp-xs text-amber-800 dark:text-amber-300">
            <strong>This link is the credential.</strong> Whoever opens it can set this
            account&apos;s password, once, until it expires. Returned because{' '}
            <code className="font-mono">security.invitation-return-token</code> is enabled —
            turn it off in production.
          </p>
          {result.token && (
            <button
              type="button"
              className="text-erp-xs underline text-amber-800 dark:text-amber-300"
              onClick={() => copy(result.token!, 'token')}
            >
              {copied === 'token' ? 'Token copied' : 'Copy the raw token instead'}
            </button>
          )}
        </div>
      ) : (
        <Note tone="info">
          The link is not shown because{' '}
          <code className="font-mono">security.invitation-return-token</code> is off. That is
          the correct production setting — the recipient gets it by SMS.
        </Note>
      )}
    </div>
  )
}

function Channel({ icon, label, ok, okText, badText }: {
  icon: React.ReactNode
  label: string
  ok: boolean
  okText: string
  badText: string
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-erp-xs border rounded-md px-2 py-1 ${
      ok
        ? 'border-emerald-300 text-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900'
        : 'border-red-300 text-red-800 bg-red-50 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900'
    }`}>
      {icon} {label} — {ok ? okText : badText}
    </span>
  )
}
